import { useEffect, useState } from "react";

import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import type { PossibleDuplicate } from "../../../api/types/clientDirectory";
import type { SelectedClient } from "../types";
import { toSelectedClient } from "../utils";

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;

/** A picker row: the client plus the records the Clients list flags it against. */
export type ClientOption = SelectedClient & {
  possibleDuplicates: PossibleDuplicate[];
};

export type ClientSearch = {
  clients: ClientOption[];
  /** Every client in the firm, from the last unfiltered load. */
  total: number | null;
  isLoading: boolean;
  error: string | null;
};

/** The firm's clients matching `query`, newest first when the query is blank. */
export const useClientSearch = (query: string): ClientSearch => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    setIsLoading(true);
    const timer = window.setTimeout(
      async () => {
        try {
          const response = await ForeignNationalsAPI.list({
            q: q || undefined,
            limit: SEARCH_LIMIT,
            sort: "newest",
            include_meta: "duplicates"
          });
          if (cancelled) return;
          setClients(
            response.foreign_nationals.map((fn) => ({
              ...toSelectedClient(fn),
              possibleDuplicates: fn.list_meta?.possible_duplicates ?? []
            }))
          );
          if (!q && typeof response.total_count === "number")
            setTotal(response.total_count);
          setError(null);
        } catch (e) {
          if (cancelled) return;
          setClients([]);
          setError(e instanceof Error ? e.message : "Failed to load clients.");
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      },
      q ? SEARCH_DEBOUNCE_MS : 0
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  return { clients, total, isLoading, error };
};
